<template>
  <div class="backgroundColor pa-6" style="min-height: 100vh;">
    <header class="mb-6 d-flex align-center">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            color="grey darken-1"
            class="px-0 mr-4"
            @click="volverBandeja"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </template>
        <span>Volver a la Gestión de Entregas</span>
      </v-tooltip>

      <h1 class="text-h4 font-weight-bold primary--text mb-0 mr-4">
        Historial Entregas Venta: <span class="font-weight-regular">{{ folioVenta }}</span>
      </h1>

      <v-chip small dark :color="macroChipColor" class="font-weight-medium">
        {{ orderData ? orderData.macro_status_description : 'Cargando...' }}
      </v-chip>
    </header>

    <v-row v-if="isLoading" justify="center" align="center" style="min-height: 50vh;">
      <v-col class="text-center" cols="12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <div class="mt-4 grey--text text--darken-1 text-body-2">
          Compilando bitácora y saldos del resguardo...</div>
      </v-col>
    </v-row>

    <v-row v-else-if="orderData">

      <v-col cols="12" md="8">

        <v-card outlined class="pa-5 mb-6">
          <div class="d-flex align-center mb-2">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">
              Balance Consolidado de Artículos
            </h2>
          </div>
          <p class="grey--text text--darken-1 mb-4 text-body-2">
            Estado y saldo acumulado neto de los productos comprados sin stock en esta orden.
          </p>

          <v-simple-table fixed-header height="300px" class="elevation-0">
            <template v-slot:default>
              <thead class="grey lighten-4">
                <tr>
                  <th class="text-left text-uppercase font-weight-bold grey--text text--darken-2">
                    Producto</th>
                  <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
                    Comprado</th>
                  <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
                    Entregado</th>
                  <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2">
                    Saldo Actual</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="prod in orderData.balance_products"
                  :key="prod.product_id"
                  :class="{'green lighten-5': prod.current_balance === 0}"
                  class="transition-swing"
                >
                  <td class="py-3" style="max-width: 250px;">
                    <div class="text-truncate font-weight-bold text-body-2
                      grey--text text--darken-4" :title="prod.name">
                      {{ prod.name }}
                    </div>
                    <div class="text-truncate text-caption grey--text text--darken-1 mt-1">
                      <span class="font-weight-bold grey--text text--darken-3">{{ prod.sku }}</span>
                      <span class="mx-1">&bull;</span> {{ prod.brand }}
                      <span class="mx-1">&bull;</span> {{ prod.model }}
                    </div>
                  </td>

                  <td class="text-center text-body-2 grey--text text--darken-3">
                    {{ prod.original_ordered }} pz
                  </td>

                  <td class="text-center text-body-2 grey--text text--darken-3">
                    {{ prod.total_delivered }} pz
                  </td>

                  <td class="text-center text-body-2 font-weight-black"
                    :class="prod.current_balance === 0 ? 'success--text'
                    : 'grey--text text--darken-4'"
                  >
                    {{ prod.current_balance }} pz
                  </td>

                </tr>
              </tbody>

            </template>
          </v-simple-table>
        </v-card>

        <v-card outlined class="pa-5">
          <div class="d-flex align-center mb-4">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">
              Línea de Tiempo de Entregas de Producto
            </h2>
          </div>

          <v-card-text v-if="!orderData.timeline_dispatches
            || orderData.timeline_dispatches.length === 0"
            class="text-center py-8 grey--text">
            No se registran visitas ni entregas físicas para este folio todavía.
          </v-card-text>

          <v-timeline v-else dense clipped class="custom-delivery-timeline">
            <v-timeline-item
              v-for="dispatch in orderData.timeline_dispatches"
              :key="dispatch.dispatch_id"
              color="secondary"
              small
              fill-dot
            >
              <v-card outlined class="elevation-1 grey lighten-5 pa-4">
                <div class="d-flex justify-space-between align-start flex-wrap mb-4">

                  <div class="d-flex flex-column align-start">
                    <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-4 mb-1">
                      Entrega #{{ dispatch.visit_number }}
                    </h3>

                    <div class="text-caption grey--text text--darken-2
                      d-flex align-center flex-wrap">
                      <v-icon small class="mr-1">mdi-calendar-clock</v-icon>
                      {{ formatFullDate(dispatch.dispatch_date) }} hrs.

                      <span class="mx-2">&bull;</span>

                      <v-icon small class="mr-1">mdi-store</v-icon>
                      Sucursal: <strong class="ml-1">{{ dispatch.dispatch_store_name || 'N/A' }}</strong>

                      <span class="mx-2">&bull;</span>

                      <v-icon small class="mr-1">mdi-account-tie</v-icon>
                      Entrega: <strong class="ml-1">{{ dispatch.employee_name }}</strong>
                    </div>
                  </div>

                  <div class="d-flex flex-column align-end mt-2 mt-sm-0">
                    <v-chip
                      v-if="dispatch.ine_validated"
                      small
                      color="success"
                      dark
                      class="font-weight-medium mb-1"
                    >
                      <v-icon left small>mdi-account-card-details-outline</v-icon>
                      Identificación Validada
                    </v-chip>

                    <div class="text-caption grey--text text--darken-3">
                      <span class="font-weight-bold grey--text text--darken-1">Documento:</span>
                      <span class="text-uppercase ml-1">
                        {{ dispatch.identity_document_folio }}
                      </span>
                    </div>
                  </div>

                </div>

                <div class="white pa-3 rounded border mb-2">
                  <div class="text-caption font-weight-bold text-uppercase
                    grey--text text--darken-1 mb-2">
                    Artículos retirados en esta visita:
                  </div>
                  <ul class="pl-4 text-body-2">
                    <li v-for="item in dispatch.items" :key="item.product_id" class="mb-1">
                      <strong>
                        {{ item.quantity_delivered }} pz</strong> &times; {{ item.name }}
                      <span class="grey--text text-caption">({{ item.sku }})</span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="dispatch.notes"
                  class="bg-slate-50 pa-3 rounded border border-slate-200 text-body-2 mt-3"
                >
                  <div class="d-flex align-center mb-1">
                    <v-icon small color="grey darken-1" class="mr-2">
                      mdi-message-text-outline
                    </v-icon>
                    <strong class="text-slate-700">Notas de entrega:</strong>
                  </div>

                  <div class="text-slate-600 font-italic pl-7">
                    "{{ dispatch.notes }}"
                  </div>
                </div>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card>

      </v-col>

      <v-col cols="12" md="4">

        <v-card outlined class="pa-5 mb-4">
          <div class="d-flex align-center mb-4">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">Responsable Autorizado</h2>
          </div>

          <v-card color="grey lighten-4" elevation="0" class="pa-3 mb-4">
            <div class="text-body-2 mb-2">
              <span class="font-weight-bold grey--text text--darken-2">Cliente:</span>
                {{ orderData.customer.name }}
            </div>
            <v-divider class="my-2"></v-divider>
            <div class="text-body-2 mb-1">
              <span class="font-weight-bold grey--text text--darken-2">Teléfono:</span>
              {{ orderData.customer.phone || 'No Registrado' }}
            </div>
            <div class="text-body-2">
              <span class="font-weight-bold grey--text text--darken-2">Email:</span>
                {{ orderData.customer.email || 'Sin Correo' }}
            </div>
          </v-card>

          <v-divider class="my-4"></v-divider>

          <div class="text-caption grey--text text--darken-1">
            <div class="mb-1"><strong>Tienda de Compra:</strong>
              {{ orderData.purchase_store_name }}</div>
          </div>
        </v-card>

        <div class="d-flex flex-column gap-3">

          <v-btn
            v-if="orderData.macro_status_id === 2"
            color="secondary"
            block
            large
            elevation="2"
            class="mb-3 font-weight-bold"
            @click="continuarDespachoActivo"
          >
            <v-icon left>mdi-dolly</v-icon>
            Continuar Despacho
          </v-btn>

          <v-btn
            outlined
            color="primary"
            block
            large
            class="mb-3 text-none font-weight-medium bg-white"
            @click="reimprimirValeRecoleccion"
          >
            <v-icon left>mdi-printer</v-icon>
            Reimprimir Vale de Resguardo
          </v-btn>

        </div>
      </v-col>

    </v-row>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import ClientDeliveryService from '@/services/ClientDeliveryService';

export default {
  name: 'DeliveryHistoryDetail',
  data() {
    return {
      isLoading: true,
      orderData: null,
    };
  },
  computed: {
    folioVenta() {
      return this.$route.params.folio || 'N/A';
    },
    macroChipColor() {
      if (!this.orderData) return 'grey';
      // Mapeo exacto del backend (2: Parcial -> warning, 3: Entregado -> success)
      return this.orderData.macro_status_id === 3 ? 'success' : 'orange darken-2';
    },
  },
  async mounted() {
    await this.fetchHistoryDetails();
  },
  methods: {
    async fetchHistoryDetails() {
      this.isLoading = true;
      try {
        const { folio } = this.$route.params;
        const storeId = this.$store.getters.currentStoreId;

        const response = await ClientDeliveryService.getHistoryOrderDetails(folio, storeId);

        if (response.status === 'success') {
          this.orderData = response.data;
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Historial no disponible',
          text: error.response?.data?.detail || 'Ocurrió un problema al leer la bitácora física de este folio.',
          confirmButtonColor: 'primary',
        }).then(() => {
          this.volverBandeja();
        });
      } finally {
        this.isLoading = false;
      }
    },
    formatFullDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
    continuarDespachoActivo() {
      // Redirecciona al operador directo a la pantalla de steppers operativos pasándole el folio
      this.$router.push({
        name: 'order-dispatch',
        params: { folio: this.folioVenta },
      });
    },
    reimprimirValeRecoleccion() {
      const routeData = this.$router.resolve({
        name: 'resguardo-vale-ticket',
        // Asegúrate de enviar los mismos parámetros que definiste en tu router
        params: { folio: String(this.folioVenta), mode: 'todas' },
      });

      // Intentamos abrir la pestaña
      const openedTab = window.open(routeData.href, '_blank', 'noopener,noreferrer');

      // Si el navegador tiene un bloqueador de ventanas emergentes muy agresivo,
      // redirigimos en la misma pestaña como plan B.
      if (!openedTab) {
        this.$router.push({
          name: 'resguardo-vale-ticket',
          params: { folio: String(this.folioVenta), mode: 'todas' },
        });
      }
    },
    volverBandeja() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}
.border-orange-light {
  border: 1px solid #ffe0b2 !important;
}
/* Estilo estilizado para la línea de tiempo de auditoría */
.custom-delivery-timeline >>> .v-timeline-item__body {
  max-width: 100%;
}
</style>
